/**
 * ESG尽职调查组件
 * 步骤3.4: ESG尽职调查
 * 基于 PROFESSIONAL_DUE_DILIGENCE_DESIGN.md 设计规范
 */

import React from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Select,
  Radio,
  DatePicker,
  InputNumber,
  Button,
  Progress,
  Rate,
  Slider,
} from 'antd';
import { UnifiedDataTable } from '../../../../components/unified/UnifiedDataTable';
import {
  TeamOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  AlertOutlined,
  PlusOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  HeartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FormSection,
  FormSubSection,
  ConditionalField,
  WhenTrue,
  WhenFalse,
  RiskMatrix,
  DocumentUpload,
  useDDAutoSave,
  useDDValidation,
  DDValidationRules,
  AutoSaveIndicator,
} from './shared';
import type {
  ESGDueDiligenceProps,
  ESGDueDiligenceData,
  ESGPolicy,
  StakeholderEngagement,
  ESGPerformanceIndicator,
  ESGRisk,
  ESGGovernanceStructure,
  ESGDisclosure,
  RiskLevel,
} from './shared/types';

const { Option } = Select;
const { TextArea } = Input;

const ESGDueDiligence: React.FC<ESGDueDiligenceProps> = ({
  projectId,
  onStepComplete,
  onStepSave,
  readonly = false,
}) => {
  const [form] = Form.useForm<ESGDueDiligenceData>();

  // 使用专业尽调专用Hooks
  const { validateForm } = useDDValidation({
    form,
    stepType: 'esg',
  });

  const { saving, saveStatus, lastSavedText, triggerAutoSave, manualSave } =
    useDDAutoSave({
      projectId,
      stepType: 'esg',
      enabled: true,
      onSave: async data => {
        console.log('ESG DD auto saved:', data);
      },
    });

  // 监听表单变化
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    triggerAutoSave(currentValues);
  };

  // 保存草稿 - 通过统一操作栏触发
  const handleSaveDraft = async () => {
    const values = form.getFieldsValue();
    await manualSave(values);
    onStepSave?.('esg_due_diligence', values);
  };

  // 提交表单 - 通过统一操作栏触发
  const handleSubmit = async () => {
    const result = await validateForm();
    if (result.success) {
      onStepComplete?.('esg_due_diligence', result.values);
    }
  };

  // ESG政策表格列定义
  const esgPolicyColumns = [
    {
      title: 'ESG领域',
      dataIndex: 'esgArea',
      key: 'esgArea',
      width: 120,
      render: (text: string, record: ESGPolicy, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies[index] = { ...newPolicies[index], esgArea: value };
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="ESG领域"
          style={{ width: '100%' }}
        >
          <Option value="environmental">环境(E)</Option>
          <Option value="social">社会(S)</Option>
          <Option value="governance">治理(G)</Option>
        </Select>
      ),
    },
    {
      title: '政策名称',
      dataIndex: 'policyName',
      key: 'policyName',
      width: 200,
      render: (text: string, record: ESGPolicy, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies[index] = {
              ...newPolicies[index],
              policyName: e.target.value,
            };
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="ESG政策名称"
        />
      ),
    },
    {
      title: '制定日期',
      dataIndex: 'establishedDate',
      key: 'establishedDate',
      width: 120,
      render: (text: string, record: ESGPolicy, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies[index] = {
              ...newPolicies[index],
              establishedDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="制定日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '实施状态',
      dataIndex: 'implementationStatus',
      key: 'implementationStatus',
      width: 120,
      render: (text: string, record: ESGPolicy, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies[index] = {
              ...newPolicies[index],
              implementationStatus: value,
            };
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="实施状态"
          style={{ width: '100%' }}
        >
          <Option value="fully_implemented">完全实施</Option>
          <Option value="partially_implemented">部分实施</Option>
          <Option value="in_development">制定中</Option>
          <Option value="planned">计划制定</Option>
          <Option value="not_applicable">不适用</Option>
        </Select>
      ),
    },
    {
      title: '有效性评分',
      dataIndex: 'effectivenessScore',
      key: 'effectivenessScore',
      width: 120,
      render: (value: number, record: ESGPolicy, index: number) => (
        <Rate
          value={value / 2} // 转换为5星制
          onChange={val => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies[index] = {
              ...newPolicies[index],
              effectivenessScore: (val || 0) * 2,
            }; // 转换为10分制
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
          allowHalf
        />
      ),
    },
    {
      title: '政策描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (text: string, record: ESGPolicy, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies[index] = {
              ...newPolicies[index],
              description: e.target.value,
            };
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="政策内容描述"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: ESGPolicy, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const policies =
              form.getFieldValue(['esgPolicies', 'policyList']) || [];
            const newPolicies = [...policies];
            newPolicies.splice(index, 1);
            form.setFieldValue(['esgPolicies', 'policyList'], newPolicies);
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  // 利益相关者参与表格列定义
  const stakeholderColumns = [
    {
      title: '利益相关者类型',
      dataIndex: 'stakeholderType',
      key: 'stakeholderType',
      width: 150,
      render: (text: string, record: StakeholderEngagement, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders[index] = {
              ...newStakeholders[index],
              stakeholderType: value,
            };
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="利益相关者类型"
          style={{ width: '100%' }}
        >
          <Option value="shareholders">股东</Option>
          <Option value="employees">员工</Option>
          <Option value="customers">客户</Option>
          <Option value="suppliers">供应商</Option>
          <Option value="communities">社区</Option>
          <Option value="government">政府</Option>
          <Option value="ngos">非政府组织</Option>
          <Option value="media">媒体</Option>
          <Option value="investors">投资者</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '参与方式',
      dataIndex: 'engagementMethod',
      key: 'engagementMethod',
      width: 150,
      render: (text: string, record: StakeholderEngagement, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders[index] = {
              ...newStakeholders[index],
              engagementMethod: value,
            };
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="参与方式"
          style={{ width: '100%' }}
        >
          <Option value="regular_meetings">定期会议</Option>
          <Option value="surveys">问卷调查</Option>
          <Option value="workshops">研讨会</Option>
          <Option value="public_consultations">公众咨询</Option>
          <Option value="online_platforms">在线平台</Option>
          <Option value="site_visits">实地访问</Option>
          <Option value="focus_groups">焦点小组</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '参与频率',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 120,
      render: (text: string, record: StakeholderEngagement, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders[index] = {
              ...newStakeholders[index],
              frequency: value,
            };
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="参与频率"
          style={{ width: '100%' }}
        >
          <Option value="weekly">每周</Option>
          <Option value="monthly">每月</Option>
          <Option value="quarterly">每季度</Option>
          <Option value="semi_annually">每半年</Option>
          <Option value="annually">每年</Option>
          <Option value="as_needed">按需</Option>
        </Select>
      ),
    },
    {
      title: '关键议题',
      dataIndex: 'keyIssues',
      key: 'keyIssues',
      width: 200,
      render: (text: string, record: StakeholderEngagement, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders[index] = {
              ...newStakeholders[index],
              keyIssues: e.target.value,
            };
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="关键关注议题"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '反馈处理',
      dataIndex: 'feedbackHandling',
      key: 'feedbackHandling',
      width: 200,
      render: (text: string, record: StakeholderEngagement, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders[index] = {
              ...newStakeholders[index],
              feedbackHandling: e.target.value,
            };
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="反馈处理机制"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '满意度评分',
      dataIndex: 'satisfactionScore',
      key: 'satisfactionScore',
      width: 120,
      render: (value: number, record: StakeholderEngagement, index: number) => (
        <Rate
          value={value / 2} // 转换为5星制
          onChange={val => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders[index] = {
              ...newStakeholders[index],
              satisfactionScore: (val || 0) * 2,
            }; // 转换为10分制
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
          allowHalf
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: StakeholderEngagement, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const stakeholders =
              form.getFieldValue(['stakeholderEngagement', 'engagementList']) ||
              [];
            const newStakeholders = [...stakeholders];
            newStakeholders.splice(index, 1);
            form.setFieldValue(
              ['stakeholderEngagement', 'engagementList'],
              newStakeholders
            );
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 自动保存指示器 */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">ESG尽职调查</h2>
        <AutoSaveIndicator
          saving={saving}
          saveStatus={saveStatus}
          lastSavedText={lastSavedText}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        disabled={readonly}
      >
        {/* A. ESG政策框架评估 */}
        <FormSection
          title="ESG政策框架评估"
          icon={<FileTextOutlined />}
          alert={{
            message: '请全面评估公司的ESG政策框架和实施情况',
            type: 'info',
          }}
        >
          <FormSubSection title="ESG政策清单">
            <Form.Item name={['esgPolicies', 'policyList']} initialValue={[]}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const policies =
                    form.getFieldValue(['esgPolicies', 'policyList']) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={esgPolicyColumns}
                        dataSource={policies}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `esg-policy-${index}`}
                        locale={{
                          emptyText: '暂无ESG政策记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newPolicies = [
                            ...policies,
                            {
                              esgArea: '',
                              policyName: '',
                              establishedDate: '',
                              implementationStatus: '',
                              effectivenessScore: 8,
                              description: '',
                            },
                          ];
                          form.setFieldValue(
                            ['esgPolicies', 'policyList'],
                            newPolicies
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加ESG政策
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="ESG治理结构">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['esgGovernance', 'hasESGCommittee']}
                  label="是否设立ESG委员会"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>是</Radio>
                    <Radio value={false}>否</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['esgGovernance', 'hasESGOfficer']}
                  label="是否设立ESG专员"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>是</Radio>
                    <Radio value={false}>否</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['esgGovernance', 'boardESGOversight']}
                  label="董事会ESG监督"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>有</Radio>
                    <Radio value={false}>无</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <ConditionalField
              dependsOn={['esgGovernance', 'hasESGCommittee']}
              condition={value => value === true}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['esgGovernance', 'esgCommitteeMembers']}
                    label="ESG委员会成员数量"
                    rules={[DDValidationRules.required()]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="委员会成员数量"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['esgGovernance', 'esgCommitteeMeetingFrequency']}
                    label="委员会会议频率"
                    rules={[DDValidationRules.required()]}
                  >
                    <Select placeholder="选择会议频率">
                      <Option value="monthly">每月</Option>
                      <Option value="quarterly">每季度</Option>
                      <Option value="semi_annually">每半年</Option>
                      <Option value="annually">每年</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </ConditionalField>
          </FormSubSection>
        </FormSection>

        {/* B. 利益相关者参与评估 */}
        <FormSection
          title="利益相关者参与评估"
          icon={<TeamOutlined />}
          alert={{
            message: '请评估公司与各利益相关者的参与程度和沟通机制',
            type: 'warning',
          }}
        >
          <FormSubSection title="利益相关者参与清单">
            <Form.Item
              name={['stakeholderEngagement', 'engagementList']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const stakeholders =
                    form.getFieldValue([
                      'stakeholderEngagement',
                      'engagementList',
                    ]) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={stakeholderColumns}
                        dataSource={stakeholders}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `stakeholder-${index}`}
                        locale={{
                          emptyText:
                            '暂无利益相关者参与记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newStakeholders = [
                            ...stakeholders,
                            {
                              stakeholderType: '',
                              engagementMethod: '',
                              frequency: '',
                              keyIssues: '',
                              feedbackHandling: '',
                              satisfactionScore: 8,
                            },
                          ];
                          form.setFieldValue(
                            ['stakeholderEngagement', 'engagementList'],
                            newStakeholders
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加利益相关者参与
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* C. ESG绩效指标分析 */}
        <FormSection
          title="ESG绩效指标分析"
          icon={<SafetyCertificateOutlined />}
          alert={{
            message: '请分析公司的ESG关键绩效指标和表现水平',
            type: 'info',
          }}
        >
          <FormSubSection title="环境指标(E)">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'environmental',
                    'carbonEmissionIntensity',
                  ]}
                  label="碳排放强度(吨CO2/万元营收)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={4}
                    placeholder="碳排放强度"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'environmental',
                    'energyConsumptionIntensity',
                  ]}
                  label="能耗强度(千瓦时/万元营收)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="能耗强度"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'environmental',
                    'waterConsumptionIntensity',
                  ]}
                  label="水耗强度(吨/万元营收)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="水耗强度"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'environmental',
                    'wasteRecyclingRate',
                  ]}
                  label="废物回收率(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="废物回收率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'environmental',
                    'renewableEnergyRatio',
                  ]}
                  label="可再生能源使用比例(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="可再生能源比例"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="社会指标(S)">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['esgPerformance', 'social', 'employeeTurnoverRate']}
                  label="员工流失率(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="员工流失率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'social',
                    'trainingHoursPerEmployee',
                  ]}
                  label="人均培训时长(小时/年)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={1}
                    placeholder="人均培训时长"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['esgPerformance', 'social', 'workplaceAccidentRate']}
                  label="工伤事故率(‰)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="工伤事故率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['esgPerformance', 'social', 'genderDiversityRatio']}
                  label="性别多样性比例(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="女性员工比例"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'social',
                    'communityInvestmentRatio',
                  ]}
                  label="社区投资比例(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="占营收比例"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'social',
                    'customerSatisfactionScore',
                  ]}
                  label="客户满意度评分"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    max={10}
                    precision={1}
                    placeholder="客户满意度(1-10分)"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="治理指标(G)">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'governance',
                    'independentDirectorRatio',
                  ]}
                  label="独立董事比例(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="独立董事比例"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'governance',
                    'boardMeetingAttendanceRate',
                  ]}
                  label="董事会出席率(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="董事会出席率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'governance',
                    'auditCommitteeIndependence',
                  ]}
                  label="审计委员会独立性(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="审计委员会独立性"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'governance',
                    'executiveCompensationTransparency',
                  ]}
                  label="高管薪酬透明度"
                  rules={[DDValidationRules.required()]}
                >
                  <Select placeholder="选择透明度水平">
                    <Option value="full_disclosure">完全披露</Option>
                    <Option value="partial_disclosure">部分披露</Option>
                    <Option value="limited_disclosure">有限披露</Option>
                    <Option value="no_disclosure">未披露</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={[
                    'esgPerformance',
                    'governance',
                    'whistleblowerProtection',
                  ]}
                  label="举报人保护机制"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>健全</Radio>
                    <Radio value={false}>不健全</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* D. ESG信息披露质量评估 */}
        <FormSection
          title="ESG信息披露质量评估"
          icon={<FileTextOutlined />}
          alert={{
            message: '请评估公司ESG信息披露的完整性、准确性和及时性',
            type: 'warning',
          }}
        >
          <FormSubSection title="披露完整性评估">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgDisclosure',
                    'completeness',
                    'environmentalDisclosure',
                  ]}
                  label="环境信息披露完整性"
                  rules={[DDValidationRules.required()]}
                >
                  <Slider
                    min={0}
                    max={10}
                    marks={{
                      0: '0分',
                      5: '5分',
                      10: '10分',
                    }}
                    step={0.5}
                    tooltip={{ formatter: value => `${value}分` }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['esgDisclosure', 'completeness', 'socialDisclosure']}
                  label="社会信息披露完整性"
                  rules={[DDValidationRules.required()]}
                >
                  <Slider
                    min={0}
                    max={10}
                    marks={{
                      0: '0分',
                      5: '5分',
                      10: '10分',
                    }}
                    step={0.5}
                    tooltip={{ formatter: value => `${value}分` }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'esgDisclosure',
                    'completeness',
                    'governanceDisclosure',
                  ]}
                  label="治理信息披露完整性"
                  rules={[DDValidationRules.required()]}
                >
                  <Slider
                    min={0}
                    max={10}
                    marks={{
                      0: '0分',
                      5: '5分',
                      10: '10分',
                    }}
                    step={0.5}
                    tooltip={{ formatter: value => `${value}分` }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="披露质量评估">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['esgDisclosure', 'quality', 'dataAccuracy']}
                  label="数据准确性评估"
                  rules={[DDValidationRules.required()]}
                >
                  <Select placeholder="选择数据准确性水平">
                    <Option value="high_accuracy">高准确性</Option>
                    <Option value="moderate_accuracy">中等准确性</Option>
                    <Option value="low_accuracy">低准确性</Option>
                    <Option value="questionable">存疑</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['esgDisclosure', 'quality', 'disclosureTimeliness']}
                  label="披露及时性评估"
                  rules={[DDValidationRules.required()]}
                >
                  <Select placeholder="选择披露及时性水平">
                    <Option value="timely">及时</Option>
                    <Option value="moderately_timely">基本及时</Option>
                    <Option value="delayed">延迟</Option>
                    <Option value="significantly_delayed">严重延迟</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* E. ESG风险评估 */}
        <FormSection
          title="ESG风险评估"
          icon={<AlertOutlined />}
          alert={{
            message: '请基于ESG尽职调查结果，全面评估ESG相关风险',
            type: 'info',
          }}
        >
          <FormSubSection title="ESG风险矩阵">
            <Form.Item
              name={['esgRiskAssessment', 'riskMatrix']}
              initialValue={[]}
            >
              <RiskMatrix
                riskCategories={[
                  { value: 'climate_risk', label: '气候风险' },
                  {
                    value: 'environmental_compliance_risk',
                    label: '环境合规风险',
                  },
                  { value: 'social_license_risk', label: '社会许可风险' },
                  { value: 'labor_relations_risk', label: '劳资关系风险' },
                  { value: 'governance_risk', label: '治理风险' },
                  { value: 'reputation_risk', label: '声誉风险' },
                  { value: 'regulatory_risk', label: '监管风险' },
                ]}
                readonly={readonly}
                emptyText="暂无ESG风险评估，请点击下方按钮添加"
                addButtonText="添加ESG风险"
                showResponsibleParty={true}
                showTimeline={true}
                onChange={value => {
                  form.setFieldValue(
                    ['esgRiskAssessment', 'riskMatrix'],
                    value
                  );
                  handleFormChange();
                }}
              />
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="综合ESG评估结论">
            <Form.Item
              name={[
                'esgRiskAssessment',
                'overallAssessment',
                'esgMaturityLevel',
              ]}
              label="ESG管理成熟度"
              rules={[DDValidationRules.required()]}
            >
              <Select placeholder="请选择ESG管理成熟度水平">
                <Option value="advanced">先进水平</Option>
                <Option value="mature">成熟水平</Option>
                <Option value="developing">发展水平</Option>
                <Option value="basic">基础水平</Option>
                <Option value="inadequate">不足水平</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name={[
                'esgRiskAssessment',
                'overallAssessment',
                'detailedAssessment',
              ]}
              label="详细ESG评估"
              rules={[
                DDValidationRules.required(),
                DDValidationRules.legalOpinion(100),
              ]}
            >
              <TextArea
                rows={6}
                placeholder="请详细阐述ESG尽职调查的结论和建议（至少100字）"
              />
            </Form.Item>

            <Form.Item
              name={[
                'esgRiskAssessment',
                'overallAssessment',
                'recommendations',
              ]}
              label="改进建议"
            >
              <TextArea rows={4} placeholder="请提出具体的ESG管理改进建议" />
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* 文档上传区域 */}
        <FormSection
          title="相关文档"
          icon={<FileTextOutlined />}
          alert={{
            message:
              '发行人和中介机构都可以上传相关文档。发行人提供基础材料，中介机构补充尽职调查报告和专业意见。',
            type: 'info',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="esg_dd_policies"
                title="ESG政策文件"
                description="ESG政策、制度、程序文件等"
                maxFileSize={20}
                maxFileCount={15}
                allowedFileTypes={['.pdf', '.doc', '.docx']}
                readonly={readonly}
              />
            </Col>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="esg_dd_reports"
                title="ESG报告文件"
                description="可持续发展报告、ESG评级报告、第三方认证等"
                maxFileSize={20}
                maxFileCount={20}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                readonly={readonly}
              />
            </Col>
          </Row>
        </FormSection>

        {/* 禁止在组件内部添加操作按钮！ */}
        {/* 所有操作必须通过统一底部操作栏处理 */}
      </Form>
    </div>
  );
};

export default ESGDueDiligence;
